import React, { useEffect, useRef, useState } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useGQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { glossary } from '@sorare/core/src/lib/glossary';

type Props = {
  placeholder?: string;
  withIcon?: boolean;
  autoFocus?: boolean;
};

const Root = styled(Horizontal).attrs({ gap: 0 })`
  width: 100%;

  & > * {
    width: 100%;
  }
`;

export const SearchBox = (props: Props) => {
  const { placeholder, withIcon = true, autoFocus } = props;
  const { formatMessage } = useIntl();
  const { query, refine } = useSearchBox();
  const [value, setValue] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track when the value coming from the React state changes to synchronize it with InstantSearch.
  useDebounce(
    () => {
      if (query !== value) {
        refine(value);
      }
    },
    300,
    [query, value]
  );

  // Track when the InstantSearch query changes to synchronize it with the React state.
  useEffect(() => {
    // We bypass the state update if the input is focused to avoid concurrent updates when typing.
    if (document.activeElement !== inputRef.current && query !== value) {
      setValue(query);
    }
    // We don't want to track when the React state value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return (
    <Root>
      <SearchInput
        fullWidth
        small
        rounded
        value={value}
        inputRef={inputRef}
        onChange={handleChange}
        placeholder={placeholder || formatMessage(glossary.search)}
        withIcon={withIcon}
        autoFocus={autoFocus}
        name="search-box"
      />
    </Root>
  );
};

export const BackendSearchBox = (props: Props) => {
  const { placeholder, withIcon = true, autoFocus } = props;
  const { query, refineQuery } = useGQLSearchContext()!;
  const { formatMessage } = useIntl();
  const [value, setValue] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track when the value coming from the React state changes to synchronize it with InstantSearch.
  useDebounce(
    () => {
      if (query !== value) {
        refineQuery(value);
      }
    },
    300,
    [query, value]
  );

  // Track when the InstantSearch query changes to synchronize it with the React state.
  useEffect(() => {
    // We bypass the state update if the input is focused to avoid concurrent updates when typing.
    if (document.activeElement !== inputRef.current && query !== value) {
      setValue(query);
    }
    // We don't want to track when the React state value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return (
    <Root>
      <SearchInput
        fullWidth
        small
        rounded
        value={value}
        inputRef={inputRef}
        onChange={handleChange}
        placeholder={placeholder || formatMessage(glossary.search)}
        withIcon={withIcon}
        autoFocus={autoFocus}
        name="search-box"
      />
    </Root>
  );
};
